import { useState, useContext, useEffect } from "react";
import "@/styles/AppHeaderStyles.scss"
import { Link } from "react-router-dom";
import homepageLogo from "@/resources/original/logo-dnb.svg";
import mobileToggleButton from "@/resources/optimized/icon-menu.svg";
import mobileCloseButton from "@/resources/optimized/icon-menu-close.svg";
import { loginMenuLinks } from "../header/content";
import { ChevronLeftSvg } from "../../resources/chevron-left";
import { useTranslation } from "react-i18next";

type MenuItem = {
    label?: string;
    children?: MenuItem[];
    class?: string;
    url?: string;
    onNewTab?: boolean;
    linkTag?: boolean;
};


const HeaderMobileComponent = ({ countryCode }: { countryCode: string }): JSX.Element => {
    const { t } = useTranslation();
    const enterpriseTitle = t("businessDirectory.enterpriseTitle");
    const smallBusinessTitle = t("businessDirectory.smallBusinessTitle");
    const searchOptionsTitle = t("searchOptions");
    const siteContentTitle = t("businessDirectory.siteContentTab");
    const dunsNumberTitle = t("businessDirectory.dunsNumberTitle");
    const businessDirectoryTitle = t("businessDirectory.businessDirectoryTitle");
    const serviceCenterTitle = t("businessDirectory.serviceCenterTitle");
    const loginTitle = t("businessDirectory.loginTitle");
    const bdLandingPageRedirectionLink = t("businessDirectory.bdLandingPageRedirectionLink");
    const smallBusinessLink = t("businessDirectory.smallBusinessLink");
    const companyProfileTab = t('businessDirectory.companyProfileTab');
    const companyProfileLink = t('businessDirectory.companyProfileLink');
    const siteContentLink = t("businessDirectory.siteContentLink");
    const businessCreditURL = t('businessCreditURL');
    const businessGrowthURL = t('businessGrowthURL');
    const businessRiskURL = t('businessRiskURL');
    const dunsNumberURL = t('dunsNumberURL');
    const enterpriseURL = t('enterpriseURL');
    const globeHeaderIconURL = t('globeHeaderIconURL');
    const publicSectorURL = t('publicSectorURL');
    const resourcesHeaderURL = t('resourcesHeaderURL');
    const serviceCenterURL = t('serviceCenterURL');
    const contactProfilesPage = t('contactProfilesPage');
    const marketPlacePage = t('marketPlacePage');
	const dunsNumberMythsURL = t('dunsNumberMythsURL');

    const menuData: MenuItem[] = [
        {
            label: searchOptionsTitle,
            children: [
                { label: siteContentTitle, class: 'sub-menu search-sub-menu', url: siteContentLink, linkTag: false },
                { label: businessDirectoryTitle, class: 'sub-menu search-sub-menu', url: bdLandingPageRedirectionLink, linkTag: true },
                { label: t("contactDirectory.contact_directory"), class: 'sub-menu search-sub-menu', url: contactProfilesPage, linkTag: true },
                { label: t("businessDirectory.marketplace"), class: 'sub-menu search-sub-menu', url: marketPlacePage, linkTag: true },
                { label: companyProfileTab, class: 'sub-menu search-sub-menu', url: companyProfileLink, linkTag: true }
            ],
            class: 'main-menu search-button',
        },
        { label: enterpriseTitle, class: 'main-menu enterprise-column', url: enterpriseURL },
    ];
    const currentCountryCode = countryCode as keyof typeof loginMenuLinks;
    const currentLinks = loginMenuLinks[currentCountryCode];
    const { section1Heading, section1Data, section2Heading, section2Data } = currentLinks
    if (countryCode === 'us') {
        menuData.push(
            { label: dunsNumberTitle, class: 'main-menu duns-menu', url: dunsNumberMythsURL },
            { label: 'Business Credit', class: 'main-menu duns-menu', url: businessCreditURL },
            { label: 'Business Growth', class: 'main-menu duns-menu', url: businessGrowthURL },
            { label: 'Business Risk', class: 'main-menu duns-menu', url: businessRiskURL },
            { label: 'Resources', class: 'main-menu duns-menu lastChild', url: resourcesHeaderURL },
            { label: dunsNumberTitle, class: 'main-menu business-menu dunsNumberMenu', url: dunsNumberURL, linkTag: true },
            { label: businessDirectoryTitle, class: 'main-menu business-menu', url: bdLandingPageRedirectionLink, linkTag: true },
            { label: serviceCenterTitle, class: 'main-menu business-menu', url: serviceCenterURL },
            {
                label: loginTitle,
                class: 'main-menu business-menu login-menu',
                children: [
                    {
                        label: section1Heading,
                        class: "sub-menu",
                        children: section1Data.map(item => ({
                            label: item.linkName,
                            url: item.url,
                            onNewTab: item.onNewTab,
                            class: "sub-menu",
                        })),
                    },
                    {
                        label: section2Heading,
                        class: "sub-menu",
                        children: section2Data?.map(item => ({
                            label: item.linkName,
                            url: item.url,
                            onNewTab: item.onNewTab,
                            class: "sub-menu",
                        })),
                    },
                ]
            },
            { label: '', class: 'main-menu business-menu globe-icon-menu', url: globeHeaderIconURL },
        );
    }
    else {
        menuData.push(
            { label: serviceCenterTitle, class: 'main-menu business-menu', url: serviceCenterURL },
            { label: dunsNumberTitle, class: 'main-menu business-menu dunsNumberMenu', url: dunsNumberURL, linkTag: true },
            { label: businessDirectoryTitle, class: 'main-menu business-menu', url: bdLandingPageRedirectionLink, linkTag: true },
            {
                label: loginTitle,
                class: 'main-menu business-menu login-menu',
                children: [
                    {
                        label: section1Heading,
                        class: "sub-menu",
                        children: section1Data.map(item => ({
                            label: item.linkName,
                            url: item.url,
                            onNewTab: item.onNewTab,
                            class: "sub-menu",
                        })),
                    },
                    {
                        label: section2Heading,
                        class: "sub-menu",
                        children: section2Data.map(item => ({
                            label: item.linkName,
                            url: item.url,
                            onNewTab: item.onNewTab,
                            class: "sub-menu",
                        })),
                    },
                ]
            },
            { label: '', class: 'main-menu business-menu globe-icon-menu', url: globeHeaderIconURL }
        );
    }
    const [isNavOpen, setIsNavOpen] = useState(false);
    const [menuStack, setMenuStack] = useState<MenuItem[][]>([menuData]);
    const homeRedirection = t("homeRedirection");

    const toggleNav = () => {
        setIsNavOpen(!isNavOpen);
        if (!isNavOpen) setMenuStack([menuData]);
    };

    const handleItemClick = (item: MenuItem) => {
        if (item.children) {
            setMenuStack(prev => [...prev, item.children!]);
        }
    };

    const handleBack = () => {
        if (menuStack.length > 1) {
            setMenuStack(prev => prev.slice(0, -1));
        }
    };

    const currentMenu = menuStack[menuStack.length - 1];

    return (
        <>
            {/* {useMinifiedHeaderBoolean ? (
                <></>
            ) : ( */}
            <div className="mobile-header-container">
                <div className="header-bar">
                    <div className="logo">
                        <a
                            aria-label="Homepage link"
                            className="consolidatedMinifiedHeaderLogoLink"
                            href={homeRedirection}
                            onClick={(e) => {
                                e.preventDefault();
                                window.location.href = homeRedirection;
                            }}
                        >
                            <img
                                alt="D&B logo"
                                className="consolidatedHeaderLogo"
                                src={homepageLogo}
                            />
                        </a>
                    </div>
                    <button className="menu-toggle" onClick={toggleNav}>
                        <img alt="Menu toggle button" src={isNavOpen ? mobileCloseButton : mobileToggleButton} />
                    </button>
                </div>

                <div className={`mobile-menu ${isNavOpen ? 'open' : ''}`}>
                    <div className="menu-content">
                        {menuStack.length > 1 && (
                            <button className="back-button" onClick={handleBack}>
                                <ChevronLeftSvg /> {menuStack.length === 2 && (<span>Main Menu</span>)}
                            </button>
                        )}
                        <ul className="menu-list">
                            {currentMenu.map((item, idx) => {
                                if (countryCode !== 'us' && (item.label === t("contactDirectory.contact_directory") || item.label === t("businessDirectory.marketplace") || (item.label === businessDirectoryTitle && item?.class?.includes('search-sub-menu')))) {
                                    return null;
                                }
                                if (countryCode === 'us' && item.label === companyProfileTab) {
                                    return null;
                                }
                                // If the item is a login section header (e.g., "My D&B Products" or "My Company Information")
                                // and it has no actual links under it, do not render its <li> element.
                                // This check is specific to items that are sub-menus and match the login section headings.
                                if (currentLinks &&
                                    item.class?.includes('sub-menu') && // Ensures we are targeting a sub-menu item
                                    (item.label === currentLinks.section1Heading || item.label === currentLinks.section2Heading) && // Matches the specific login section heading
                                    (!item.children || item.children.length === 0) // Checks if there are no actual links
                                ) {
                                    return null; // Skip rendering this list item
                                }
                                return (
                                    <li key={idx} onClick={() => handleItemClick(item)} className={item?.class}>
                                        {item?.class?.includes('search-button') && <>
                                            <span className="search-icon"></span>
                                            {item.label}
                                        </>}
                                        {item?.class?.includes('enterprise-column') && <>
                                            <a className="enterprise-link inactive-link enterprise" href={item?.url}>{item.label}</a>
                                            <a href={smallBusinessLink}>{smallBusinessTitle}</a>
                                            {countryCode === 'us' && (<a className="inactive-link" href={publicSectorURL}>Public Sector</a>)}
                                        </>}
                                        {item?.class?.includes('duns-menu') && <>
                                            <a className="consolidatedHeaderLink" href={item?.url}>{item.label}</a>
                                        </>}
                                        {item?.class?.includes('business-menu') && <>
                                            {item?.class?.includes('login-menu') && <span className="login-icon"></span>}
                                            {item?.linkTag === true && item?.url ? (<Link to={item?.url} onClick={toggleNav}>{item.label}</Link>) : (<a href={item?.url}>{item?.class?.includes('globe-icon-menu') ? <span className="globe-icon"></span> : item.label}</a>)}
                                        </>}
                                        {item?.class?.includes('sub-menu') && <>
                                            {item?.linkTag === true && item?.url ? (<Link className="consolidatedHeaderLink" to={item?.url} onClick={toggleNav}>{item.label}</Link>) : (
                                                <a
                                                    className="consolidatedHeaderLink"
                                                    href={item?.url || "javascript:void(0)"}
                                                    onClick={e => {
                                                        if (!item?.url) e.preventDefault();
                                                    }
                                                    }>
                                                    {item.label}
                                                </a>
                                            )}
                                        </>}
                                        {item.children && <span className="arrow"></span>}
                                    </li>
                                )
                            })}
                        </ul>
                    </div>
                </div>
                {isNavOpen && <div className="overlay" onClick={toggleNav} />}
            </div >
        </>
    )
}

export default HeaderMobileComponent;
